/**
 * DraftChangesModal Component
 * 
 * Modal for viewing and managing unpublished draft changes
 * Allows users to review, remove individual changes, or discard all
 */

import { useState, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Button } from './ui'
import useDraftStore from '../stores/draftStore'
import { resourceMeta, resourceDisplayName, OPERATION_LABEL } from '../utils/resourceLabels'

const PharmacyEditModal = lazy(() => import('./PharmacyEditModal'))
const DrugProgramEditModal = lazy(() => import('./DrugProgramEditModal'))
const PhysicianEditModal = lazy(() => import('./PhysicianEditModal'))

export default function DraftChangesModal({ isOpen, onClose }) {
  const navigate = useNavigate()
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [editingDraft, setEditingDraft] = useState(null)
  const {
    changes,
    removeChange,
    updateChange,
    clearDrafts,
    getChangeSummary
  } = useDraftStore()
  
  const summary = getChangeSummary()
  
  const handleRemoveChange = (id) => {
    removeChange(id)
  }
  
  const handleClearAll = () => {
    if (confirmingClear) {
      clearDrafts()
      setConfirmingClear(false)
      onClose()
    } else {
      setConfirmingClear(true)
    }
  }
  
  const getChangeDescription = (change) => {
    const { label, color } = resourceMeta(change.resource_type)
    const name = resourceDisplayName(change)
    const opText = OPERATION_LABEL[change.operation] || change.operation

    return {
      text: `${opText}: ${label} "${name}"`,
      section: label,
      sectionColor: color
    }
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="max-w-3xl w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Unpublished Changes</h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              ✕
            </button>
          </div>
          
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {summary.creates}
              </div>
              <div className="text-sm text-muted-foreground">Created</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {summary.updates}
              </div>
              <div className="text-sm text-muted-foreground">Updated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {summary.deletes}
              </div>
              <div className="text-sm text-muted-foreground">Deleted</div>
            </div>
          </div>
          
          {/* Change list */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">Changes</h3>
            
            {changes.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No unpublished changes
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                <ul className="divide-y">
                  {changes.map((change) => {
                    const desc = getChangeDescription(change)
                    return (
                      <li key={change.id} className="p-3 hover:bg-muted/50">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${desc.sectionColor}`}>
                                {desc.section}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                change.operation === 'create' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100' :
                                change.operation === 'update' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100' :
                                'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100'
                              }`}>
                                {change.operation}
                              </span>
                            </div>
                            <div className="font-medium">{desc.text}</div>
                            {change.description && (
                              <div className="text-sm text-muted-foreground mt-1">
                                {change.description}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground mt-1">
                              {new Date(change.timestamp).toLocaleString()}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {change.operation !== 'delete' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingDraft(change)}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveChange(change.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
          
          {/* Warning for clear all */}
          {confirmingClear && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <div className="flex items-start gap-2">
                <span className="text-destructive text-xl">⚠️</span>
                <div className="text-sm">
                  <div className="font-medium text-destructive mb-1">
                    Are you sure you want to discard all changes?
                  </div>
                  <div className="text-muted-foreground">
                    This action cannot be undone. All {changes.length} unpublished changes will be permanently lost.
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-between gap-3">
            <div className="flex gap-2">
              {changes.length > 0 && (
                <>
                  <Button
                    variant={confirmingClear ? "destructive" : "outline"}
                    onClick={handleClearAll}
                  >
                    {confirmingClear ? 'Click to Confirm Discard' : 'Discard All Changes'}
                  </Button>
                  {confirmingClear && (
                    <Button
                      variant="ghost"
                      onClick={() => setConfirmingClear(false)}
                    >
                      Cancel
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-3">
              {changes.length > 0 && (
                <Button
                  onClick={() => {
                    onClose()
                    navigate('/releases')
                  }}
                >
                  🚀 Publish Release
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Edit draft modal — renders the appropriate edit form pre-populated with draft data */}
      {editingDraft && (
        <Suspense fallback={null}>
          {editingDraft.resource_type === 'pharmacy' && (
            <PharmacyEditModal
              isOpen={true}
              onClose={() => setEditingDraft(null)}
              pharmacy={editingDraft.new_data}
              onSave={(updatedData) => {
                updateChange(editingDraft.id, { new_data: updatedData })
                setEditingDraft(null)
              }}
              isDraftEdit={true}
            />
          )}
          {editingDraft.resource_type === 'drug_program' && (
            <DrugProgramEditModal
              isOpen={true}
              onClose={() => setEditingDraft(null)}
              program={editingDraft.new_data}
              onSave={(updatedData) => {
                updateChange(editingDraft.id, { new_data: updatedData })
                setEditingDraft(null)
              }}
              isDraftEdit={true}
            />
          )}
          {editingDraft.resource_type === 'physician' && (
            <PhysicianEditModal
              isOpen={true}
              onClose={() => setEditingDraft(null)}
              physician={editingDraft.new_data}
              onSave={(updatedData) => {
                updateChange(editingDraft.id, { new_data: updatedData })
                setEditingDraft(null)
              }}
              isDraftEdit={true}
            />
          )}
        </Suspense>
      )}
    </div>
  )
}

